import { Injectable, inject } from '@angular/core';
import { Observable, BehaviorSubject, map } from 'rxjs';
import { SmartOrpcClientService } from './smart-orpc-client.service';

export interface StateOption {
  value: string;
  label: string;
}

export interface CityOption {
  value: string;
  label: string;
  state: string;
}

@Injectable({
  providedIn: 'root',
})
export class GeographicDataService {
  private smartClient = inject(SmartOrpcClientService);

  private statesSubject = new BehaviorSubject<StateOption[]>([]);
  private citiesSubject = new BehaviorSubject<CityOption[]>([]);
  private allCitiesSubject = new BehaviorSubject<CityOption[]>([]);

  public states$ = this.statesSubject.asObservable();
  public cities$ = this.citiesSubject.asObservable();
  public allCities$ = this.allCitiesSubject.asObservable();

  private isStatesLoaded = false;
  private isCitiesLoaded = false;

  /**
   * Load states from backend using abbreviations (siglas)
   * Returns state codes like 'SP', 'RJ', 'MG'
   * This is the default method used by initialize()
   */
  async loadStates(): Promise<void> {
    return this.loadStatesFullNames();
  }

  /**
   * Load states with abbreviations (siglas)
   * Returns state codes like 'SP', 'RJ', 'MG'
   */
  async loadStatesAbbreviations(): Promise<void> {
    if (this.isStatesLoaded) return;

    try {
      const statesData =
        await this.smartClient.client.ibgeMunicipios.statesAbbreviations();
      const stateOptions: StateOption[] = statesData.map((item) => ({
        value: item.state,
        label: item.stateuf, // Using abbreviation as both value and label
      }));

      this.statesSubject.next(stateOptions);
      this.isStatesLoaded = true;
    } catch (error) {
      console.error('Error loading states abbreviations:', error);
      this.statesSubject.next([]);
    }
  }

  /**
   * Load states with full names
   * Returns full state names like 'São Paulo', 'Rio de Janeiro', 'Minas Gerais'
   */
  async loadStatesFullNames(): Promise<void> {
    if (this.isStatesLoaded) return;

    try {
      const statesData = await this.smartClient.client.ibgeMunicipios.states();
      const stateOptions: StateOption[] = statesData.map((item) => ({
        value: item.state,
        label: item.state, // Using full name as both value and label
      }));

      this.statesSubject.next(stateOptions);
      this.isStatesLoaded = true;
    } catch (error) {
      console.error('Error loading states full names:', error);
      this.statesSubject.next([]);
    }
  }

  /**
   * Load all cities from backend with state abbreviations
   * Returns cities with state codes like 'SP', 'RJ', 'MG'
   * This is the default method used by initialize()
   */
  async loadCities(): Promise<void> {
    return this.loadCitiesWithAbbreviations();
  }

  /**
   * Load cities with state abbreviations
   * Returns cities with state codes like 'SP', 'RJ', 'MG'
   */
  async loadCitiesWithAbbreviations(): Promise<void> {
    if (this.isCitiesLoaded) return;

    try {
      const citiesData =
        await this.smartClient.client.ibgeMunicipios.citiesWithAbbreviations();
      const cityOptions: CityOption[] = citiesData.map((item) => ({
        value: item.city,
        label: item.city,
        state: item.state, // Using state abbreviation (e.g., 'SP')
      }));

      this.allCitiesSubject.next(cityOptions);
      this.citiesSubject.next(cityOptions); // Initially show all cities
      this.isCitiesLoaded = true;
    } catch (error) {
      console.error('Error loading cities with abbreviations:', error);
      this.allCitiesSubject.next([]);
      this.citiesSubject.next([]);
    }
  }

  /**
   * Load cities with full state names
   * Returns cities with full state names like 'São Paulo', 'Rio de Janeiro'
   */
  async loadCitiesWithFullStateNames(): Promise<void> {
    if (this.isCitiesLoaded) return;

    try {
      const citiesData = await this.smartClient.client.ibgeMunicipios.cities();
      const cityOptions: CityOption[] = citiesData.map((item) => ({
        value: item.city,
        label: item.city,
        state: item.state, // Using full state name
      }));

      this.allCitiesSubject.next(cityOptions);
      this.citiesSubject.next(cityOptions); // Initially show all cities
      this.isCitiesLoaded = true;
    } catch (error) {
      console.error('Error loading cities with full state names:', error);
      this.allCitiesSubject.next([]);
      this.citiesSubject.next([]);
    }
  }

  /**
   * Filter cities by selected state
   */
  filterCitiesByState(selectedState: string): void {
    const allCities = this.allCitiesSubject.value;

    if (!selectedState) {
      // Show all cities if no state selected
      this.citiesSubject.next(allCities);
    } else {
      // Filter cities by state
      const filteredCities = allCities.filter(
        (city) => city.state === selectedState,
      );
      this.citiesSubject.next(filteredCities);
    }
  }

  /**
   * Get states as options for select components
   */
  getStatesOptions(): Observable<StateOption[]> {
    return this.states$;
  }

  /**
   * Get cities as options for select components
   */
  getCitiesOptions(): Observable<CityOption[]> {
    return this.cities$;
  }

  /**
   * Initialize data loading
   */
  async initialize(): Promise<void> {
    await this.initializeWithFullNames();
  }

  /**
   * Initialize data loading with state abbreviations
   * States: 'SP', 'RJ', 'MG', etc.
   */
  async initializeWithAbbreviations(): Promise<void> {
    await Promise.all([
      this.loadStatesAbbreviations(),
      this.loadCitiesWithAbbreviations(),
    ]);
  }

  /**
   * Initialize data loading with full state names
   * States: 'São Paulo', 'Rio de Janeiro', 'Minas Gerais', etc.
   */
  async initializeWithFullNames(): Promise<void> {
    await Promise.all([
      this.loadStatesFullNames(),
      this.loadCitiesWithFullStateNames(),
    ]);
  }

  /**
   * Reset loading flags to allow reloading with different format
   */
  resetLoadingFlags(): void {
    this.isStatesLoaded = false;
    this.isCitiesLoaded = false;
  }
}
